import React, { useState, useEffect } from "react";
import Header from "../Components/Header";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Feature from "../Components/Feature";
import Footer from "../Components/Footer";
import "./Cart.css";
import paymentIcons from "../assets/images/paymentIcons.png";
import { Link, useNavigate } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import axios from "../api/axios";

export default function Checkout() {
  const navigate = useNavigate();
  const token = localStorage.getItem("access_token");
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const userId = user ? user.id : null;

  // Loading & Error States
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Cart States
  const [cartItems, setCartItems] = useState([]);
  const [cartSummary, setCartSummary] = useState(null);

  // Saved Addresses
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");

  // Geographical Data
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Form Fields for New Address
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [addressText, setAddressText] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [apartment, setApartment] = useState("");
  const [selectedStateId, setSelectedStateId] = useState("");
  const [selectedCityId, setSelectedCityId] = useState("");

  // Billing Address Same
  const [billingSame, setBillingSame] = useState(true);

  // Payment
  const [paymentMethod, setPaymentMethod] = useState("cash_on_delivery");

  // Fetch Page Data on Mount
  useEffect(() => {
    if (!token || !userId) {
      navigate("/login");
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        // 1. Fetch Cart List
        const cartRes = await axios.post("/carts", { user_id: userId });
        let items = [];
        if (cartRes.data && cartRes.data.data) {
          cartRes.data.data.forEach((shop) => {
            if (shop.cart_items) {
              items = [...items, ...shop.cart_items];
            }
          });
        }
        setCartItems(items);

        // 2. Fetch Cart Summary
        const summaryRes = await axios.post("/cart-summary", { user_id: userId });
        setCartSummary(summaryRes.data);

        // 3. Fetch Saved Addresses
        const addressRes = await axios.get("/user/shipping/address");
        if (addressRes.data && addressRes.data.data) {
          setAddresses(addressRes.data.data);
          const defaultAddr = addressRes.data.data.find((addr) => addr.set_default === 1);
          if (defaultAddr) {
            setSelectedAddressId(defaultAddr.id.toString());
          } else if (addressRes.data.data.length > 0) {
            setSelectedAddressId(addressRes.data.data[0].id.toString());
          } else {
            setSelectedAddressId("new");
          }
        } else {
          setSelectedAddressId("new");
        }

        // 4. Fetch States (India is country ID 101)
        const statesRes = await axios.get("/states-by-country/101");
        if (statesRes.data && statesRes.data.data) {
          setStates(statesRes.data.data);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading checkout details:", err);
        setErrorMsg("Failed to load checkout information. Please verify your connection.");
        setLoading(false);
      }
    };

    loadData();
  }, [token, userId, navigate]);

  // Load Cities when State changes
  const handleStateChange = async (e) => {
    const stateId = e.target.value;
    setSelectedStateId(stateId);
    setSelectedCityId("");
    setCities([]);
    if (!stateId) return;

    try {
      const res = await axios.get(`/cities-by-state/${stateId}`);
      if (res.data && res.data.data) {
        setCities(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  // Submit Order
  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!token || !userId) {
      navigate("/login");
      return;
    }

    if (cartItems.length === 0) {
      setErrorMsg("Your cart is empty.");
      return;
    }

    try {
      setSubmitting(true);
      let addressId = selectedAddressId;

      // Create a new address if 'new' is selected
      if (selectedAddressId === "new") {
        if (!firstName || !lastName || !addressText || !postalCode || !phone || !email || !selectedStateId || !selectedCityId) {
          setErrorMsg("Please fill in all required shipping address fields.");
          setSubmitting(false);
          return;
        }

        const fullAddressString = `${addressText}${apartment ? ", " + apartment : ""}${company ? " (" + company + ")" : ""}`;

        // Call Create Address API
        const createAddrRes = await axios.post("/user/shipping/create", {
          address: fullAddressString,
          country_id: 101, // India
          state_id: parseInt(selectedStateId),
          city_id: parseInt(selectedCityId),
          postal_code: postalCode,
          phone: phone,
        });

        if (!createAddrRes.data.result) {
          throw new Error(createAddrRes.data.message || "Failed to create shipping address.");
        }

        // Fetch user addresses again to find the ID of the new address
        const updatedAddrRes = await axios.get("/user/shipping/address");
        if (updatedAddrRes.data && updatedAddrRes.data.data && updatedAddrRes.data.data.length > 0) {
          // Take the newest address (highest ID)
          const sorted = updatedAddrRes.data.data.sort((a, b) => b.id - a.id);
          addressId = sorted[0].id.toString();
          setAddresses(updatedAddrRes.data.data);
          setSelectedAddressId(addressId);
        } else {
          throw new Error("Could not retrieve created shipping address.");
        }
      }

      // Link address to cart
      const linkRes = await axios.post("/update-address-in-cart", {
        user_id: userId,
        address_id: parseInt(addressId),
      });

      if (!linkRes.data.result) {
        throw new Error(linkRes.data.message || "Failed to assign address to cart.");
      }

      // Set shipping type in cart to calculate cost
      const shipRes = await axios.post("/update-shipping-type-in-cart", {
        user_id: userId,
        shipping_type: "home_delivery",
        shipping_id: 0,
      });

      if (!shipRes.data.result) {
        throw new Error(shipRes.data.message || "Failed to update shipping cost.");
      }

      // Submit the actual Order
      let orderRes;
      if (paymentMethod === "cash_on_delivery") {
        orderRes = await axios.post("/payments/pay/cod", {
          payment_type: "cash_on_delivery",
        });
      } else {
        // Online payment (simulated as Stripe / manual API order)
        orderRes = await axios.post("/order/store", {
          payment_type: "stripe",
        });
      }

      if (orderRes.data.result) {
        setSuccessMsg(orderRes.data.message || "Order placed successfully!");
        // Clear local storage cart indicators if any, then navigate to thank you page
        setTimeout(() => {
          navigate("/thankyou", { state: { orderId: orderRes.data.combined_order_id } });
        }, 1500);
      } else {
        throw new Error(orderRes.data.message || "Failed to place order.");
      }
    } catch (err) {
      console.error("Order Checkout error:", err);
      setErrorMsg(err.response?.data?.message || err.message || "An error occurred while placing the order.");
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Header mode="dark" />

      <div className="checkout-page py-5">
        <div className="container-fluid px-4">
          <div className="breadcrumb_title detail_page_breadcrumb mb-4">
            <Breadcrumb>
              <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
              <Breadcrumb.Item href="/products">Shop</Breadcrumb.Item>
              <Breadcrumb.Item active>Checkout</Breadcrumb.Item>
            </Breadcrumb>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-dark" role="status">
                <span className="visually-hidden">Loading checkout details...</span>
              </div>
              <h5 className="mt-3 text-muted">Loading Checkout...</h5>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="text-center py-5 my-5">
              <h3 className="mb-3">Your Shopping Cart is Empty</h3>
              <p className="text-muted">You must add products to your cart before you can check out.</p>
              <Link to="/products" className="btn btn-dark mt-3 px-4 py-2">
                Go to Shop
              </Link>
            </div>
          ) : (
            <div className="row g-5">
              {/* LEFT FORM */}
              <div className="col-lg-7">
                <div className="checkout_inforamtion bg-white p-4 rounded shadow-sm">
                  <h4 className="section-title mb-4 border-bottom pb-2">Shipping Information</h4>

                  {errorMsg && <div className="alert alert-danger mb-4">{errorMsg}</div>}
                  {successMsg && <div className="alert alert-success mb-4">{successMsg}</div>}

                  <form onSubmit={handlePlaceOrder}>
                    {/* Saved Addresses Section */}
                    {addresses.length > 0 && (
                      <div className="mb-4">
                        <h6 className="text-muted mb-3">Select a Saved Address</h6>
                        <div className="row g-3 mb-3">
                          {addresses.map((addr) => (
                            <div className="col-md-6" key={addr.id}>
                              <div
                                className={`card h-100 p-3 address-card cursor-pointer border ${
                                  selectedAddressId === addr.id.toString()
                                    ? "border-dark bg-light"
                                    : "border-light-subtle"
                                }`}
                                onClick={() => setSelectedAddressId(addr.id.toString())}
                                style={{ borderRadius: "8px", position: "relative" }}
                              >
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="saved_address"
                                    checked={selectedAddressId === addr.id.toString()}
                                    onChange={() => setSelectedAddressId(addr.id.toString())}
                                    id={`addr-${addr.id}`}
                                  />
                                  <label
                                    className="form-check-label fw-bold d-block cursor-pointer"
                                    htmlFor={`addr-${addr.id}`}
                                  >
                                    Address #{addr.id}
                                  </label>
                                </div>
                                <div className="small mt-2 text-muted">
                                  <p className="mb-1">{addr.address}</p>
                                  <p className="mb-1">
                                    {addr.city_name}, {addr.state_name}, {addr.country_name} - {addr.postal_code}
                                  </p>
                                  <p className="mb-0 fw-semibold">Phone: {addr.phone}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="saved_address"
                            checked={selectedAddressId === "new"}
                            onChange={() => setSelectedAddressId("new")}
                            id="addr-new"
                          />
                          <label className="form-check-label fw-bold cursor-pointer" htmlFor="addr-new">
                            Ship to a different address
                          </label>
                        </div>
                      </div>
                    )}

                    {/* New Address Form fields */}
                    {selectedAddressId === "new" && (
                      <div className="row g-3 mt-2 border-top pt-4">
                        <h6 className="text-muted mb-2">New Shipping Address</h6>
                        <div className="col-md-12">
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Country / Region"
                            value="India"
                            disabled
                            style={{ backgroundColor: "#f8f9fa" }}
                          />
                        </div>
                        <div className="col-md-6">
                          <input
                            className="form-control"
                            type="text"
                            placeholder="First Name *"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Last Name *"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-md-12">
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Company (optional)"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                          />
                        </div>
                        <div className="col-md-12">
                          <textarea
                            className="form-control"
                            placeholder="Street Address *"
                            rows="2"
                            value={addressText}
                            onChange={(e) => setAddressText(e.target.value)}
                            required
                          ></textarea>
                        </div>
                        <div className="col-md-12">
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Apartment, suite, unit etc. (optional)"
                            value={apartment}
                            onChange={(e) => setApartment(e.target.value)}
                          />
                        </div>

                        {/* State Dropdown */}
                        <div className="col-md-6">
                          <select
                            className="form-select"
                            value={selectedStateId}
                            onChange={handleStateChange}
                            required
                          >
                            <option value="">Select State *</option>
                            {states.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* City Dropdown */}
                        <div className="col-md-6">
                          <select
                            className="form-select"
                            value={selectedCityId}
                            onChange={(e) => setSelectedCityId(e.target.value)}
                            required
                            disabled={!selectedStateId}
                          >
                            <option value="">Select City *</option>
                            {cities.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-md-4">
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Pincode *"
                            value={postalCode}
                            onChange={(e) => setPostalCode(e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-md-8">
                          <input
                            className="form-control"
                            type="tel"
                            placeholder="Phone Number *"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-md-12">
                          <input
                            className="form-control"
                            type="email"
                            placeholder="Email Address *"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    )}

                    <div className="billing_adrs mt-4 border-top pt-4">
                      <h5 className="section-title mb-3">Billing Address</h5>
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={billingSame}
                          onChange={(e) => setBillingSame(e.target.checked)}
                          id="billing-same"
                        />
                        <label className="form-check-label cursor-pointer" htmlFor="billing-same">
                          Same as shipping address
                        </label>
                      </div>
                      {!billingSame && (
                        <div className="alert alert-info py-2 small">
                          We will use the shipping address as billing address for mock billing checkout.
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="btn btn-dark w-100 mt-4 py-3 fw-bold shadow-sm"
                      disabled={submitting}
                      style={{ fontSize: "16px", borderRadius: "8px" }}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Processing Order...
                        </>
                      ) : (
                        `Confirm and Place Order`
                      )}
                    </button>
                  </form>
                </div>
              </div>

              {/* RIGHT SUMMARY */}
              <div className="col-lg-5">
                <div className="summary-section p-4 bg-light rounded shadow-sm">
                  <h4 className="border-bottom pb-2 mb-4">Order Summary</h4>

                  {/* Cart items list */}
                  <div
                    className="cart-summary-items mb-4"
                    style={{ maxHeight: "350px", overflowY: "auto" }}
                  >
                    {cartItems.map((item) => (
                      <div className="product d-flex py-3 border-bottom align-items-center" key={item.id}>
                        <div
                          style={{
                            width: "70px",
                            height: "70px",
                            borderRadius: "6px",
                            overflow: "hidden",
                            backgroundColor: "#fff",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={item.product_thumbnail_image}
                            alt={item.product_name}
                            className="w-100 h-100"
                            style={{ objectFit: "cover" }}
                          />
                        </div>
                        <div className="ms-3 flex-grow-1">
                          <h6 className="mb-1 text-truncate" style={{ maxWidth: "220px", fontSize: "14px" }}>
                            {item.product_name}
                          </h6>
                          <div className="text-muted small">
                            {item.variation && <p className="mb-0">Variant: {item.variation}</p>}
                            <p className="mb-0">Qty: {item.quantity}</p>
                          </div>
                        </div>
                        <div className="check_price fw-bold text-end ms-2" style={{ fontSize: "15px" }}>
                          {item.price}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Discount / Coupon code */}
                  <div className="d-flex discount_input mb-4 gap-2">
                    <input className="form-control" placeholder="Discount Code" />
                    <button className="btn btn-dark px-3">Apply</button>
                  </div>

                  {/* Price breakdown */}
                  <div className="price-breakdown border-bottom pb-3 mb-3">
                    <div className="price-row d-flex justify-content-between mb-2">
                      <span className="text-muted">Subtotal</span>
                      <span className="fw-semibold">{cartSummary?.sub_total || "₹0"}</span>
                    </div>

                    <div className="price-row d-flex justify-content-between mb-2">
                      <span className="text-muted">Tax</span>
                      <span className="fw-semibold">{cartSummary?.tax || "₹0"}</span>
                    </div>

                    <div className="price-row d-flex justify-content-between mb-2">
                      <span className="text-muted">Shipping</span>
                      <span className="fw-semibold text-success">
                        {cartSummary?.shipping_cost === "₹0" ? "Free" : cartSummary?.shipping_cost || "Free"}
                      </span>
                    </div>
                  </div>

                  <div className="total-row d-flex justify-content-between align-items-center mb-4 pt-1">
                    <h5 className="mb-0">Total</h5>
                    <h4 className="mb-0 fw-bold">{cartSummary?.grand_total || "₹0"}</h4>
                  </div>

                  {/* Payment selection */}
                  <div className="payment-box p-3 bg-white rounded border">
                    <h5 className="mb-2 fw-semibold" style={{ fontSize: "16px" }}>
                      Payment Option
                    </h5>
                    <p className="small text-muted mb-3">
                      <LockOutlinedIcon style={{ fontSize: "16px" }} className="me-1" />
                      All transactions are secure and encrypted.
                    </p>

                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="payment"
                        id="pay-cod"
                        checked={paymentMethod === "cash_on_delivery"}
                        onChange={() => setPaymentMethod("cash_on_delivery")}
                      />
                      <label className="form-check-label cursor-pointer fw-semibold" htmlFor="pay-cod">
                        Cash on Delivery
                      </label>
                    </div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="payment"
                        id="pay-online"
                        checked={paymentMethod === "online"}
                        onChange={() => setPaymentMethod("online")}
                      />
                      <label
                        className="form-check-label cursor-pointer d-flex align-items-center gap-2 fw-semibold"
                        htmlFor="pay-online"
                      >
                        Online Card Payment
                        <img src={paymentIcons} alt="Cards" style={{ maxHeight: "20px" }} />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Feature />
      <Footer />
    </div>
  );
}
